// GK Brandify — central content source
import {
  Compass, FlaskConical, Target, PenTool, Code2, ShieldCheck, Rocket, LifeBuoy,
} from "lucide-react";
import { SERVICE_ICONS } from "../components/ServiceIcons";

export const BRAND = {
  name: "GK Brandify",
  tagline: "Building Brands. Shaping the Future.",
  founder: "Gowtham V",
  logo: "/gk-logo.png",
  phone: "+91 7975621314",
  phoneRaw: "917975621314",
  email: "gowthamvr1718@gmail.com",
  address: "AMCO Layout, Kodigehalli, Bangalore – 560092",
  socials: [
    { label: "Instagram", href: "https://instagram.com" },
    { label: "LinkedIn", href: "https://linkedin.com" },
    { label: "Facebook", href: "https://facebook.com" },
    { label: "Behance", href: "https://behance.net" },
    { label: "Dribbble", href: "https://dribbble.com" },
  ],
};

export const NAV_LINKS = [
  { label: "Home", id: "home" },
  { label: "Services", id: "services" },
  { label: "Portfolio", id: "portfolio" },
  { label: "About", id: "about" },
  { label: "Pricing", id: "pricing" },
  { label: "Contact", id: "contact" },
];

// Honest, non-fabricated value props (no invented client/project counts)
export const TRUST_BADGES = [
  "100% Custom Design",
  "On-Time Delivery",
  "NDA Friendly",
  "Post-Launch Support",
];

export const INDUSTRIES = [
  "Startups", "Fintech", "E-Commerce", "SaaS",
  "Real Estate", "Healthcare", "Hospitality", "D2C Brands",
];

export const SERVICES = [
  { icon: SERVICE_ICONS.BrandIdentity, title: "Brand Identity", desc: "Complete visual systems — logo, palette, typography and voice that make you instantly recognisable.", benefits: ["Consistent everywhere", "Builds instant trust"] },
  { icon: SERVICE_ICONS.LogoDesign, title: "Logo Design", desc: "Distinctive, timeless marks engineered for recognition across every screen and surface.", benefits: ["Memorable & scalable", "Full file kit delivered"] },
  { icon: SERVICE_ICONS.WebsiteDesign, title: "Website Design", desc: "Editorial, conversion-focused interfaces that look premium and guide visitors to action.", benefits: ["Higher conversions", "Mobile-first layouts"] },
  { icon: SERVICE_ICONS.WebsiteDevelopment, title: "Website Development", desc: "Fast, secure, scalable builds using modern React best practices and clean code.", benefits: ["90+ performance", "Easy to maintain"] },
  { icon: SERVICE_ICONS.UIUX, title: "UI / UX Design", desc: "Research-led, human-centred experiences that reduce friction and increase engagement.", benefits: ["Intuitive journeys", "Fewer drop-offs"] },
  { icon: SERVICE_ICONS.SEO, title: "SEO", desc: "Technical and on-page SEO so the right customers discover you on Google.", benefits: ["Rank & get found", "Sustainable traffic"] },
  { icon: SERVICE_ICONS.GoogleBusiness, title: "Google Business Profile", desc: "Optimised local presence so you show up on Maps and Search with reviews that convert.", benefits: ["Local visibility", "More calls & visits"] },
  { icon: SERVICE_ICONS.SocialMedia, title: "Social Media Branding", desc: "Cohesive, scroll-stopping social templates and a visual language that grows your audience.", benefits: ["On-brand content", "Faster posting"] },
  { icon: SERVICE_ICONS.MarketingConsult, title: "Digital Marketing Consultation", desc: "Clear, actionable strategy to turn your brand into predictable growth and revenue.", benefits: ["Focused roadmap", "Measurable goals"] },
];

export const WHY = [
  { title: "Premium Design", desc: "Every pixel is deliberate — nothing is left to chance." },
  { title: "Fast Delivery", desc: "Studio-grade craft, shipped on an ambitious timeline." },
  { title: "Mobile Responsive", desc: "Flawless on mobile, tablet and desktop, every time." },
  { title: "SEO Optimized", desc: "Built to be discovered, indexed and ranked on Google." },
  { title: "Secure Development", desc: "Modern, hardened builds with security best practices." },
  { title: "Ongoing Support", desc: "A partnership that continues well beyond launch." },
  { title: "Affordable Pricing", desc: "Transparent tiers with clear value at every level." },
  { title: "Modern Technology", desc: "A future-proof React stack that scales with you." },
];

export const PROCESS = [
  { icon: Compass, step: "01", title: "Discovery", desc: "We immerse in your vision, market and ambition." },
  { icon: FlaskConical, step: "02", title: "Research", desc: "Competitors, audience and data shape the direction." },
  { icon: Target, step: "03", title: "Strategy", desc: "A clear brand and product strategy is defined." },
  { icon: PenTool, step: "04", title: "Design", desc: "Concepts crafted into a distinctive, ownable identity." },
  { icon: Code2, step: "05", title: "Development", desc: "Pixel-perfect, performant engineering brings it to life." },
  { icon: ShieldCheck, step: "06", title: "Testing", desc: "Rigorous QA across devices, speed and accessibility." },
  { icon: Rocket, step: "07", title: "Launch", desc: "A confident, flawless release to the world." },
  { icon: LifeBuoy, step: "08", title: "Support", desc: "Ongoing optimisation and dependable aftercare." },
];

// Honest promises, not invented track-record numbers
export const STATS = [
  { value: 100, suffix: "%", label: "Custom-Designed, No Templates" },
  { value: 48, suffix: "h", label: "Response Within 48 Hours" },
  { value: 9, suffix: "", label: "Specialist Services" },
  { value: 5, suffix: "★", label: "Our Quality Benchmark" },
];

// Concept & sample work — clearly not presented as real client case studies
export const PORTFOLIO = [
  { title: "Aurum Fintech", category: "Brand Identity", industry: "Fintech", year: "Concept", tags: ["Identity", "Guidelines", "Motion"], tech: ["Illustrator", "Figma", "After Effects"], desc: "A concept identity system for a private wealth platform — gold-forward, restrained and premium.", outcome: "Demonstrates a cohesive, trust-building brand system from logo to motion." },
  { title: "Noir Studio", category: "Website Design", industry: "Architecture", year: "Concept", tags: ["Web", "Art Direction"], tech: ["React", "Framer Motion", "Tailwind"], desc: "A cinematic portfolio concept for a luxury architecture studio with immersive scroll storytelling.", outcome: "Shows how editorial layout and motion elevate perceived brand value." },
  { title: "Élan Commerce", category: "UI / UX", industry: "E-Commerce", year: "Concept", tags: ["UX", "Design System"], tech: ["Figma", "React"], desc: "A concept storefront experience with fluid motion and a clean, conversion-focused UI language.", outcome: "Illustrates a frictionless path from browse to checkout." },
  { title: "Solaire Suite", category: "Logo Collection", industry: "Wellness", year: "Concept", tags: ["Logo", "Typography"], tech: ["Illustrator"], desc: "A family of monogram marks exploring metallic gold gradients and negative-space geometry.", outcome: "Explores versatile, scalable mark systems for a brand family." },
  { title: "Vertex System", category: "Website Development", industry: "SaaS", year: "Concept", tags: ["Web App", "Design Ops"], tech: ["React", "Node", "Tailwind"], desc: "A concept enterprise design system engineered for consistency, speed and elegance at scale.", outcome: "Highlights maintainable, component-driven front-end architecture." },
  { title: "Meridian Local", category: "SEO & GBP", industry: "Real Estate", year: "Concept", tags: ["SEO", "Local", "GBP"], tech: ["Search Console", "GBP"], desc: "A concept local-visibility playbook: optimised Google Business Profile and on-page SEO.", outcome: "Shows the approach to ranking and generating local enquiries." },
];

export const TESTIMONIALS = [
  { name: "Sample Client", role: "Startup Founder", quote: "GK Brandify didn't just design our brand — they defined our direction. The result feels genuinely world-class.", rating: 5, sample: true },
  { name: "Sample Client", role: "E-Commerce Owner", quote: "Every detail was obsessed over. The new website looks premium and finally reflects the quality of what we sell.", rating: 5, sample: true },
  { name: "Sample Client", role: "SaaS Marketing Lead", quote: "Clear strategy, beautiful design and smooth delivery. Our brand now commands attention in every room.", rating: 5, sample: true },
  { name: "Sample Client", role: "Local Business Owner", quote: "Professional, responsive and creative. They elevated our identity to a level we didn't think was possible.", rating: 5, sample: true },
];

export const PRICING = [
  {
    name: "Starter",
    price: "₹49K",
    period: "/ project",
    tagline: "For emerging brands finding their voice.",
    features: ["Logo Design", "Mini Brand Guidelines", "5-Page Website", "Mobile Responsive", "Basic SEO Setup", "30 Days Support"],
    featured: false,
  },
  {
    name: "Professional",
    price: "₹1.5L",
    period: "/ project",
    tagline: "For ambitious brands ready to scale.",
    features: ["Full Brand Identity", "Custom Website (10+ pages)", "UI/UX Design", "Advanced SEO", "Google Business Profile", "Social Media Branding Kit", "90 Days Support"],
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    tagline: "For market leaders demanding the exceptional.",
    features: ["Everything in Professional", "Advanced Web Development", "Digital Marketing Consultation", "Conversion Optimisation", "Dedicated Project Lead", "Priority Support", "Ongoing Partnership"],
    featured: false,
  },
];

export const PRICING_COMPARISON = [
  { feature: "Logo & Brand Identity", starter: "Basic", professional: "Full", enterprise: "Full+" },
  { feature: "Website Pages", starter: "5", professional: "10+", enterprise: "Custom" },
  { feature: "UI/UX Design", starter: false, professional: true, enterprise: true },
  { feature: "SEO", starter: "Basic", professional: "Advanced", enterprise: "Advanced+" },
  { feature: "Google Business Profile", starter: false, professional: true, enterprise: true },
  { feature: "Social Media Branding", starter: false, professional: true, enterprise: true },
  { feature: "Marketing Consultation", starter: false, professional: false, enterprise: true },
  { feature: "Support", starter: "30 Days", professional: "90 Days", enterprise: "Ongoing" },
];

export const FAQS = [
  { q: "How long does a typical project take?", a: "Most brand and website projects take 2–6 weeks depending on scope. We start with a clear timeline during discovery and keep you updated at every milestone." },
  { q: "What does a project cost?", a: "Projects typically range from ₹50,000 to ₹5,00,000+ based on scope. Our Starter, Professional and Enterprise tiers give a clear starting point, and we tailor a quote to your goals." },
  { q: "Do you work with startups and small businesses?", a: "Absolutely. We work with startups, local businesses and growing companies — matching the right service and budget to your stage." },
  { q: "Will my website be mobile responsive and SEO ready?", a: "Yes. Every build is fully responsive across mobile, tablet and desktop, and comes with SEO fundamentals so you can be found on Google." },
  { q: "Do you provide support after launch?", a: "Yes. Every package includes a support window, and we offer ongoing partnership plans for maintenance, updates and growth." },
  { q: "How do we get started?", a: "Tap “Start Your Project” or message us on WhatsApp. We'll set up a free consultation to understand your goals and recommend the best path forward." },
];

export const MARQUEE_WORDS = [
  "BRAND IDENTITY", "WEBSITE DESIGN", "WEBSITE DEVELOPMENT",
  "UI/UX DESIGN", "LOGO DESIGN", "SEO",
  "SOCIAL MEDIA BRANDING", "DIGITAL MARKETING",
];
